/**
 * Solar PV Proposal — Auto-fill Card
 *
 * Drop-in card for the New / Edit Customer form. Admin uploads a proposal PDF
 * and Gemini extracts 27 fields that are then mapped onto the React-Hook-Form
 * fields. The PDF blob is held in state via the `onProposalReady` callback so
 * the parent can attach it to the customer record AFTER creation (avoids a
 * second Gemini call by re-posting with the `pre_extracted` JSON).
 *
 * Props:
 *  - form: react-hook-form instance
 *  - customerId?: string (when present, the PDF is attached immediately)
 *  - onProposalReady?: ({ file, extracted }) => void
 *      Called once extraction succeeds. The parent stores both and replays
 *      the upload (with pre_extracted JSON) post-create to persist the PDF.
 */
import React, { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Upload, Loader2, FileText, Sparkles, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { runProposalJob } from '../lib/proposalJob';

const splitName = (full) => {
  if (!full) return { first: '', last: '' };
  const parts = String(full).trim().split(/\s+/);
  if (parts.length === 1) return { first: parts[0], last: '' };
  return { first: parts[0], last: parts.slice(1).join(' ') };
};

const splitAddressPostcode = (raw, fallbackPostcode) => {
  // Try to peel a UK postcode off the end of the address (e.g. "12 High St, Bedford MK40 1AA")
  const text = String(raw || '').trim();
  const ukPcRegex = /\b([A-Z]{1,2}\d{1,2}[A-Z]?\s*\d[A-Z]{2})\b/i;
  const m = text.match(ukPcRegex);
  const postcode = (m ? m[1] : fallbackPostcode || '').toUpperCase().replace(/\s+/g, ' ').trim();
  const addressOnly = m ? text.replace(m[0], '').replace(/[,\s]+$/, '').trim() : text;
  return { address: addressOnly, postcode };
};

export const ProposalUploadCard = forwardRef(function ProposalUploadCard(
  { form, customerId, onProposalReady },
  ref,
) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [extracted, setExtracted] = useState(null);
  const [filename, setFilename] = useState('');
  const [filledCount, setFilledCount] = useState(0);

  // Expose openPicker() to parents — used by the customer-creation prompt
  // so its "Upload now" button can trigger this card's file dialog.
  useImperativeHandle(ref, () => ({
    openPicker: () => inputRef.current?.click(),
  }));

  const applyToForm = (data) => {
    // Only overwrite empty form fields so we don't clobber the admin's typing.
    const setIfEmpty = (name, value, transform = (v) => v) => {
      if (value === null || value === undefined || value === '') return false;
      const current = form.getValues(name);
      const empty = current === '' || current === null || current === undefined || current === 0 || current === '0';
      if (empty) {
        form.setValue(name, transform(value), { shouldDirty: true });
        return true;
      }
      return false;
    };

    let count = 0;

    // Customer block
    if (data.customer_name) {
      const { first, last } = splitName(data.customer_name);
      if (setIfEmpty('first_name', first)) count += 1;
      if (setIfEmpty('last_name', last)) count += 1;
    }
    const { address: addrOnly, postcode } = splitAddressPostcode(data.customer_address, data.postcode);
    if (setIfEmpty('address', addrOnly)) count += 1;
    if (setIfEmpty('postcode', postcode)) count += 1;

    // System
    if (setIfEmpty('system_size', data.installed_capacity_kwp)) count += 1;
    // Total system cost — pulled from the proposal's Bank Transfer/Card
    // Payment "Purchase Price" cell. Drives everything else (deposit,
    // final balance, contract pack totals).
    if (setIfEmpty('system_total_cost', data.purchase_price_gbp)) count += 1;

    // Panels
    if (setIfEmpty('panel_manufacturer', data.panel_manufacturer)) count += 1;
    if (setIfEmpty('panel_model', data.panel_model)) count += 1;
    if (setIfEmpty('panel_quantity', data.panel_count)) count += 1;

    // Inverter
    if (setIfEmpty('inverter_manufacturer', data.inverter_manufacturer)) count += 1;
    if (setIfEmpty('inverter_model', data.inverter_model)) count += 1;
    if (data.inverter_model) setIfEmpty('inverter_quantity', 1);

    // Battery
    if (setIfEmpty('battery_manufacturer', data.battery_manufacturer)) count += 1;
    if (setIfEmpty('battery_model', data.battery_model)) count += 1;
    if (data.battery_model) setIfEmpty('battery_quantity', 1);

    // System type checkboxes — flip on the most-specific match
    const hasBattery = !!(data.battery_usable_capacity_kwh && data.battery_usable_capacity_kwh > 0) || !!data.battery_model;
    const hasPv = !!(data.installed_capacity_kwp || data.panel_count);
    const noneSelected = !['system_type_solar', 'system_type_solar_battery', 'system_type_battery_only', 'system_type_electrical', 'system_type_ev_charger', 'system_type_other']
      .some((k) => form.getValues(k));
    if (noneSelected) {
      if (hasPv && hasBattery) form.setValue('system_type_solar_battery', true, { shouldDirty: true });
      else if (hasPv) form.setValue('system_type_solar', true, { shouldDirty: true });
      else if (hasBattery) form.setValue('system_type_battery_only', true, { shouldDirty: true });
    }

    // Additional works identified on the proposal → customer notes.
    if (data.additional_works && String(data.additional_works).trim()) {
      const block = `Additional works (from proposal):\n${String(data.additional_works).trim()}`;
      const cur = form.getValues('notes') || '';
      if (!cur.includes(block)) {
        form.setValue('notes', cur ? `${cur}\n\n${block}` : block, { shouldDirty: true });
        count += 1;
      }
    }

    return count;
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Please upload a PDF proposal');
      try { e.target.value = ''; } catch { /* noop */ }
      return;
    }
    if (file.size > 30 * 1024 * 1024) {
      toast.error('Proposal PDF exceeds 30 MB');
      try { e.target.value = ''; } catch { /* noop */ }
      return;
    }

    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const url = customerId
        ? `/dno/extract-from-proposal/start?customer_id=${encodeURIComponent(customerId)}`
        : '/dno/extract-from-proposal/start';
      const data = await runProposalJob(api, url, fd);
      const extractedData = data?.extracted || {};
      const count = applyToForm(extractedData);
      setExtracted(extractedData);
      setFilename(file.name);
      setFilledCount(count);
      onProposalReady?.({ file, extracted: extractedData });
      toast.success(
        data?.attached
          ? `Proposal attached — ${count} fields auto-filled`
          : `Proposal read — ${count} fields auto-filled. Save the customer to attach the PDF.`,
        { duration: 5000 },
      );
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || 'Proposal extraction failed');
    } finally {
      setUploading(false);
      try { e.target.value = ''; } catch { /* noop */ }
    }
  };

  return (
    <Card className="border-amber-200 bg-amber-50/40" data-testid="proposal-upload-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-base font-semibold font-['Manrope'] flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-amber-600" />
          Solar PV Proposal — Auto-fill
          <Badge variant="outline" className="ml-2 bg-white text-[10px] font-normal">Optional</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-slate-600">
          Drop in your customer's solar PV proposal PDF and we'll auto-extract 27 fields
          (customer name, address, system size, panels, inverter, battery, etc.) and fill the form below.
          Empty fields only — anything you've already typed is preserved.
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            variant="outline"
            disabled={uploading}
            onClick={() => inputRef.current?.click()}
            className="bg-white"
            data-testid="proposal-upload-btn"
          >
            {uploading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Reading proposal…
              </>
            ) : (
              <>
                <Upload className="w-4 h-4 mr-2" />
                {extracted ? 'Replace proposal' : 'Upload proposal PDF'}
              </>
            )}
          </Button>
          <input
            ref={inputRef}
            type="file"
            accept="application/pdf,.pdf"
            onChange={handleFile}
            className="hidden"
            data-testid="proposal-upload-input"
          />
          {filename && (
            <div className="flex items-center gap-1.5 text-xs text-slate-600 bg-white px-2 py-1 rounded border border-slate-200">
              <FileText className="w-3.5 h-3.5 text-amber-600" />
              {filename}
            </div>
          )}
          {extracted && (
            <div className="flex items-center gap-1.5 text-xs text-emerald-700 bg-emerald-50 px-2 py-1 rounded border border-emerald-200" data-testid="proposal-extracted-summary">
              <CheckCircle2 className="w-3.5 h-3.5" />
              {filledCount} fields filled
              {extracted.installed_capacity_kwp ? ` · ${extracted.installed_capacity_kwp} kWp` : ''}
              {extracted.battery_usable_capacity_kwh ? ` · ${extracted.battery_usable_capacity_kwh} kWh battery` : ''}
            </div>
          )}
        </div>
        {!customerId && extracted && (
          <p className="text-xs text-slate-500 italic">
            The PDF will be attached to the customer file when you click "Save Customer" below.
          </p>
        )}
      </CardContent>
    </Card>
  );
});

export default ProposalUploadCard;
